import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Save, Upload, FileText, Link2, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import { sortByName } from "@/utils/sort";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminSessionEdit() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/sessions").then((r) => {
      const s = r.data.find((s) => s.id === id);
      setSession(s || null);
    }).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveSession = useCallback(async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/sessions/${id}`, updates);
      setSession(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }, [id]);

  const handleFieldSave = () => {
    saveSession({ title: session.title, description: session.description, notes: session.notes });
  };

  // Materials
  const [linkDialog, setLinkDialog] = useState(false);
  const [linkForm, setLinkForm] = useState({ name: "", url: "" });
  const [matUploading, setMatUploading] = useState(false);
  const [matProgress, setMatProgress] = useState("");

  const handleBulkMaterialUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setMatUploading(true);
    let currentMats = [...(session.materials || [])];
    let uploaded = 0;
    for (const file of files) {
      setMatProgress(`Uploading ${uploaded + 1} of ${files.length}...`);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        currentMats = [...currentMats, { id: crypto.randomUUID(), type: "file", name: data.original_filename, storage_path: data.storage_path, content_type: data.content_type }];
        uploaded++;
      } catch {
        toast.error(`Failed: ${file.name}`);
      }
    }
    currentMats = sortByName(currentMats);
    await saveSession({ materials: currentMats });
    setMatUploading(false);
    setMatProgress("");
    if (uploaded > 0) toast.success(`${uploaded} file${uploaded > 1 ? "s" : ""} uploaded`);
    e.target.value = "";
  };

  const handleAddLink = async () => {
    const newMats = sortByName([...(session.materials || []), { id: crypto.randomUUID(), type: "link", name: linkForm.name || linkForm.url, url: linkForm.url }]);
    await saveSession({ materials: newMats });
    setLinkDialog(false);
    setLinkForm({ name: "", url: "" });
  };

  const removeMaterial = async (matId) => {
    await saveSession({ materials: (session.materials || []).filter((m) => m.id !== matId) });
  };

  // Gallery
  const [imgUploading, setImgUploading] = useState(false);
  const [imgProgress, setImgProgress] = useState("");

  const handleBulkImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setImgUploading(true);
    let currentImages = [...(session.images || [])];
    let uploaded = 0;
    for (const file of files) {
      setImgProgress(`Uploading ${uploaded + 1} of ${files.length}...`);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        currentImages = [...currentImages, { id: crypto.randomUUID(), storage_path: data.storage_path, original_filename: data.original_filename, content_type: data.content_type }];
        uploaded++;
      } catch {
        toast.error(`Failed: ${file.name}`);
      }
    }
    currentImages = sortByName(currentImages, "original_filename");
    await saveSession({ images: currentImages });
    setImgUploading(false);
    setImgProgress("");
    if (uploaded > 0) toast.success(`${uploaded} image${uploaded > 1 ? "s" : ""} uploaded`);
    e.target.value = "";
  };

  const removeImage = async (imgId) => {
    await saveSession({ images: (session.images || []).filter((x) => x.id !== imgId) });
  };

  if (loading) {
    return <AdminLayout><div className="flex items-center justify-center py-20"><div className="text-[#5C3A2E]/50">Loading...</div></div></AdminLayout>;
  }

  if (!session) {
    return <AdminLayout><div className="text-center py-20"><p className="text-[#2B2B2B]/50">Session not found</p></div></AdminLayout>;
  }

  const sortedMaterials = sortByName(session.materials || []);
  const sortedImages = sortByName(session.images || [], "original_filename");

  return (
    <AdminLayout>
      <div data-testid="admin-session-edit" className="space-y-8 animate-fade-in-up max-w-3xl">
        <div className="flex items-center justify-between">
          <Link to="/admin/sessions" className="inline-flex items-center gap-2 text-sm text-[#2B2B2B]/50 hover:text-[#5C3A2E] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Sessions
          </Link>
          <Button data-testid="save-session-fields" onClick={handleFieldSave} disabled={saving} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        {/* Details */}
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8 space-y-5">
          <h2 className="font-heading text-xl text-[#5C3A2E] font-semibold">Session Details</h2>
          <div>
            <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Title</label>
            <Input data-testid="edit-session-title" value={session.title || ""} onChange={(e) => setSession({ ...session, title: e.target.value })} className="rounded-xl border-[#EADBC8]" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Description</label>
            <Textarea data-testid="edit-session-description" value={session.description || ""} onChange={(e) => setSession({ ...session, description: e.target.value })} rows={3} className="rounded-xl border-[#EADBC8] resize-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Notes</label>
            <RichTextEditor value={session.notes || ""} onChange={(val) => setSession({ ...session, notes: val })} placeholder="Write notes for this session..." />
          </div>
        </div>

        {/* Resources & Materials */}
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
          <h2 className="font-heading text-xl text-[#5C3A2E] font-semibold mb-4">Resources & Materials</h2>
          <div className="space-y-2 mb-4">
            {sortedMaterials.map((mat) => (
              <div key={mat.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#F7F3EF]">
                {mat.type === "link" ? <Link2 className="w-4 h-4 text-[#C6A969]" /> : <FileText className="w-4 h-4 text-[#5C3A2E]" />}
                <span className="text-sm text-[#2B2B2B] flex-1">{mat.name}</span>
                <span className="text-xs text-[#2B2B2B]/30 uppercase">{mat.type}</span>
                <Button variant="ghost" size="sm" onClick={() => removeMaterial(mat.id)} className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            {sortedMaterials.length === 0 && <p className="text-sm text-[#2B2B2B]/40">No materials added yet</p>}
          </div>
          {matUploading && (
            <div className="flex items-center gap-2 text-sm text-[#5C3A2E] mb-3">
              <Loader2 className="w-4 h-4 animate-spin" /> {matProgress}
            </div>
          )}
          <div className="flex gap-2">
            <label className={`inline-flex items-center gap-2 px-4 py-2 bg-[#EADBC8] text-[#5C3A2E] rounded-full text-sm font-medium cursor-pointer hover:bg-[#EADBC8]/80 transition-colors ${matUploading ? "opacity-50 pointer-events-none" : ""}`}>
              <Upload className="w-4 h-4" /> Upload Files
              <input data-testid="session-material-upload" type="file" multiple onChange={handleBulkMaterialUpload} className="hidden" />
            </label>
            <Button data-testid="session-add-link" variant="outline" onClick={() => setLinkDialog(true)} className="border-[#EADBC8] text-[#5C3A2E] rounded-full hover:bg-[#EADBC8]/30">
              <Link2 className="w-4 h-4 mr-1.5" /> Add Link
            </Button>
          </div>
          <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
            <DialogContent className="bg-white rounded-xl max-w-sm">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-[#5C3A2E]">Add Link</DialogTitle>
                <DialogDescription className="text-[#2B2B2B]/50 text-sm">Add an external resource link</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Name</label>
                  <Input value={linkForm.name} onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })} placeholder="e.g., Pattern Template" className="rounded-xl border-[#EADBC8]" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">URL</label>
                  <Input value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} placeholder="https://..." className="rounded-xl border-[#EADBC8]" />
                </div>
                <Button onClick={handleAddLink} disabled={!linkForm.url} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">Add Link</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Image Gallery */}
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
          <h2 className="font-heading text-xl text-[#5C3A2E] font-semibold mb-4">Image Gallery</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
            {sortedImages.map((img) => (
              <div key={img.id} className="relative group aspect-square rounded-xl overflow-hidden bg-[#F7F3EF]">
                <img src={`${API_BASE}/api/files/${img.storage_path}`} alt={img.original_filename || "Gallery"} className="w-full h-full object-cover" />
                <p className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[10px] px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">{img.original_filename}</p>
                <div className="absolute top-1 right-1">
                  <Button variant="ghost" size="sm" onClick={() => removeImage(img.id)} className="opacity-0 group-hover:opacity-100 text-white bg-black/30 hover:bg-black/50 rounded-full h-7 w-7 p-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {imgUploading && (
            <div className="flex items-center gap-2 text-sm text-[#5C3A2E] mb-3">
              <Loader2 className="w-4 h-4 animate-spin" /> {imgProgress}
            </div>
          )}
          <label className={`inline-flex items-center gap-2 px-4 py-2 bg-[#EADBC8] text-[#5C3A2E] rounded-full text-sm font-medium cursor-pointer hover:bg-[#EADBC8]/80 transition-colors ${imgUploading ? "opacity-50 pointer-events-none" : ""}`}>
            <ImageIcon className="w-4 h-4" /> Upload Images
            <input data-testid="session-image-upload" type="file" accept="image/*" multiple onChange={handleBulkImageUpload} className="hidden" />
          </label>
        </div>
      </div>
    </AdminLayout>
  );
}
